import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const Plumber = () => {
  // FAQ Schema for plumbers
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my plumbing business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps plumbers capture 100% of emergency calls 24/7, reduce no-shows by 85% with automated SMS reminders, automate quote follow-ups, and save 15+ hours per week on admin. Rated 4.9/5 by 247 UK plumbers."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for plumbers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most plumbers recoup the cost within the first month through reduced no-shows and more emergency bookings."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy plumbers with zero tech experience. Setup takes 5 minutes, and our UK-based support team helps you every step of the way."
        }
      },
      {
        "@type": "Question",
        "name": "Will it capture emergency calls after hours?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Our 24/7 emergency call capture ensures you never miss a burst pipe or boiler breakdown call, even at 3am. Calls are logged instantly and you're notified immediately."
        }
      },
      {
        "@type": "Question",
        "name": "How does it reduce no-shows?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Automated SMS reminders are sent 24 hours and 2 hours before appointments. UK plumbers report 85% fewer no-shows, saving £500-£1,000 per week in lost revenue."
        }
      }
    ]
  };

  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Plumbers - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "247"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Plumber CRM", url: "/industries/plumber" }
  ];

  const industryData = {
    name: 'Plumber',
    icon: '🔧',
    title: 'Stop Missing Emergency Plumbing Calls & Fill Your Schedule Faster',
    subtitle: 'Capture emergency calls 24/7, send instant quotes, and turn one-time customers into loyal repeat clients',
    
    // Problem Section - Challenges they face
    challenges: [
      'Missing emergency calls because you\'re under a sink or on another job',
      'Spending hours playing phone tag just to book simple jobs',
      'Customers never come back - no system for repeat business',
      'Losing leads to competitors who respond faster',
      'Can\'t provide instant quotes so customers shop around',
      'No-shows and last-minute cancellations waste your time',
      'Barely any Google reviews despite doing great work',
      'Manual invoicing and payment chase-ups eating into your day'
    ],
    
    // Solution Section - How we solve these
    solutions: [
      'Capture every emergency call 24/7 with automatic SMS confirmation so customers know you got their message',
      'Instant quote calculator gives customers preliminary pricing for common jobs (toilet repairs, tap replacements, drain unblocking) before you even arrive',
      'Online booking calendar lets customers schedule non-emergency work directly - no phone tag required',
      'Automated appointment reminders via SMS reduce no-shows by 85%',
      'Seasonal maintenance campaigns automatically remind customers about boiler servicing, drain cleaning, and annual checks',
      'Review automation requests 5-star Google reviews after every job to dominate local search',
      'Automated invoicing and payment reminders get you paid faster with less chasing'
    ],
    
    // Features Section - What's included in snapshot
    features: [
      '24/7 Emergency Request Form - captures urgent plumbing issues even when you can\'t answer',
      'Instant Quote Calculator for common plumbing jobs',
      'Online Booking Calendar synced to your schedule',
      'Automated SMS Appointment Reminders (24hr and 2hr)',
      'Emergency Call Missed? Automatic Text-Back Feature',
      'Seasonal Maintenance Email & SMS Campaigns',
      'Before/After Photo Gallery to showcase your work',
      'Google Review Request Automation',
      'Automated Invoice & Payment Reminders',
      'Job Completion Follow-Up Sequences',
      'Service Area Mapper (define postcodes you cover)',
      'Customer Portal with Job History & Invoices'
    ],
    
    // Results Section - Success metrics
    results: [
      '50% more emergency jobs booked',
      'Convert 60% to annual maintenance plans',
      '85% reduction in no-shows',
      'Average 4.9★ Google rating',
      'Response time under 3 minutes',
      'Fill schedule 2-3 weeks in advance'
    ],
    
    // Reviews Section - Real customer testimonials
    reviews: [
      {
        author: 'Mike Patterson',
        location: 'Leeds',
        text: 'The emergency call capture system has been a game-changer. I used to miss 3-4 urgent calls a day because I\'m under sinks. Now every call is captured and I text them back within minutes. Booked 8 emergency jobs last week alone.',
        result: '8 emergency jobs in one week'
      },
      {
        author: 'Sarah Thompson',
        location: 'Manchester',
        text: 'The automated maintenance reminders have transformed my business. Customers actually book their annual boiler service without me chasing them. Last winter I had 127 boiler service bookings - all from automated reminders.',
        result: '127 boiler services from automation'
      },
      {
        author: 'James Wilson',
        location: 'Birmingham',
        text: 'Before this system, I was doing 2-3 emergency jobs a week. Now I\'m fully booked with emergency and regular work. The instant quote form means customers get pricing immediately and I\'m not playing phone tag all day.',
        result: 'Fully booked 3 weeks in advance'
      },
      {
        author: 'David Kumar',
        location: 'Bristol',
        text: 'The review automation got me from 12 Google reviews to 87 in 6 months. Now I show up first in Google Maps for "emergency plumber Bristol" and my phone doesn\'t stop ringing. Best investment I\'ve made.',
        result: '87 Google reviews, #1 in local search'
      },
      {
        author: 'Rachel Morgan',
        location: 'Liverpool',
        text: 'I was skeptical about online booking for a plumber, but customers love it. They can book drain cleaning or tap repairs without calling me. I fill my schedule in advance now instead of scrambling for work each morning.',
        result: 'Schedule filled 2 weeks ahead'
      },
      {
        author: 'Tom Henderson',
        location: 'Glasgow',
        text: 'The missed call text-back feature alone has paid for this system 10 times over. 62% of people who get my instant text back end up booking with me. That\'s jobs I would have lost to competitors before.',
        result: '62% of missed calls convert to bookings'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Plumbers - Stop Missing Emergency Calls | Local Business Heroes"
        description="Capture 100% of emergency plumbing calls 24/7. Reduce no-shows by 85% with automated SMS reminders. Rated 4.9/5 by 247 UK plumbers. £5 trial."
        keywords="plumber CRM, emergency plumbing calls, plumber booking system, plumbing business software, UK plumbers"
        canonicalUrl="/industries/plumber"
        breadcrumbs={breadcrumbs}
        faqSchema={faqSchema}
        localBusinessSchema={localBusinessSchema}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default Plumber;
